import { DividerType } from 'root/consts';

const DIVIDER_STYLE_KEYS = [
  'height',
  'backgroundColor',
  'backgroundSize',
  'mask',
  'backgroundImage',
  'backgroundPosition',
  'borderTop',
] as const;

export type DividerName = 'itemDivider' | 'sectionTopDivider' | 'sectionBottomDivider';

export type DividerStylesType = {
  [K in (typeof DIVIDER_STYLE_KEYS)[number]]?: string;
};

export const getDividerStyles = ({
  type,
  size,
  color,
}: {
  type: DividerType;
  size: number;
  color: string;
}): DividerStylesType => {
  switch (type) {
    case DividerType.Wave:
      return {
        height: `${4 * size}px`,
        backgroundColor: color,
        mask: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 40'%3E%3Cpath d='M-50,20 Q0,-10 50,20 Q100,50 150,20 Q200,-10 250,20' stroke='black' stroke-width='10' fill='none'/%3E%3C/svg%3E")`,
        backgroundSize: `${size * 20}px ${size * 4}px`,
      };
    case DividerType.Zigzag:
      const g = `${
        10 * size
      }px repeat-x conic-gradient(from -45deg at bottom, #0000,#000 1deg calc(89deg),#0000 90deg)`;

      return {
        backgroundColor: color,
        height: `${6 * size}px`,
        mask: `50% ${-1 * size}px/${g} exclude,50%/${g}`,
      };
    case DividerType.Checkerboard:
      const height = 4 * size;
      const halfHeight = height / 2;

      return {
        height: `${height}px`,
        backgroundImage: `linear-gradient(45deg, ${color} 25%, transparent 25%, transparent 75%, ${color} 75%), linear-gradient(45deg, ${color} 25%, transparent 25%, transparent 75%, ${color} 75%)`,
        backgroundPosition: `0 0, ${halfHeight}px ${halfHeight}px`,
        backgroundSize: `${height}px ${height}px`,
      };
    case DividerType.Solid:
    default:
      return {
        borderTop: `solid ${size}px ${color}`,
      };
  }
};

export const createPrefixedStyleName = ({
  styleParamName,
  styleName,
}: {
  styleParamName: string;
  styleName: string;
}) => {
  return `${styleParamName}-${styleName}`;
};

export const getAllDividerStylesNames = ({ styleParamName }: { styleParamName: string }) => {
  return Object.fromEntries(
    DIVIDER_STYLE_KEYS.map((styleKey) => [
      `--divider-${styleKey}`,
      `var(--${createPrefixedStyleName({ styleParamName, styleName: styleKey })})`,
    ])
  );
};

export const createPrefixedDividerStyles = ({
  dividerName,
  dividerStyles,
}: {
  dividerName: DividerName;
  dividerStyles: DividerStylesType;
}): DividerStylesType => {
  return Object.fromEntries(
    Object.entries(dividerStyles).map(([styleName, styleValue]) => [
      createPrefixedStyleName({ styleParamName: dividerName, styleName }),
      styleValue,
    ])
  );
};
